// "Create identity" out of a face selection (PRD-0031 slice 07, SC-007).
//
// First name, last name and nickname — each optional on its own, but the submit
// waits until one of them has text, because a person is never created nameless
// any more. The server enforces the same rule with a 422, and both entry points
// (the document overlay's unassigned row and the *Unassigned faces* tab) open
// this one dialog.
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export interface CreateIdentityDraft {
  firstName: string
  lastName: string
  nickname: string
}

/** What an untouched dialog starts from. */
export const EMPTY_IDENTITY_DRAFT: CreateIdentityDraft = {
  firstName: '',
  lastName: '',
  nickname: '',
}

/** True once one of the three fields holds more than whitespace. */
export function hasIdentityName(draft: CreateIdentityDraft): boolean {
  return [draft.firstName, draft.lastName, draft.nickname].some(value => value.trim().length > 0)
}

interface CreateIdentityFromFacesDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  faceCount: number
  draft: CreateIdentityDraft
  onDraftChange: (draft: CreateIdentityDraft) => void
  onSubmit: () => void
  isSubmitting: boolean
  /** The server's refusal, already a sentence — the 422 and the 409 show here. */
  error: string | null
  /** The i18n base of the title, the description and the submit: what the identity is made from. */
  copy?: string
}

export function CreateIdentityFromFacesDialog({
  open,
  onOpenChange,
  faceCount,
  draft,
  onDraftChange,
  onSubmit,
  isSubmitting,
  error,
  copy = 'identity.unassigned.create',
}: CreateIdentityFromFacesDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t(`${copy}.title`)}</DialogTitle>
          <DialogDescription>{t(`${copy}.description`, { count: faceCount })}</DialogDescription>
        </DialogHeader>

        <CreateIdentityFromFacesForm
          faceCount={faceCount}
          draft={draft}
          onDraftChange={onDraftChange}
          onSubmit={onSubmit}
          onCancel={() => onOpenChange(false)}
          isSubmitting={isSubmitting}
          error={error}
          copy={copy}
        />
      </DialogContent>
    </Dialog>
  )
}

/**
 * The dialog's body: the three fields, the rule's hint and the two buttons.
 *
 * Split out of the dialog so the validation can be rendered and asserted on its
 * own — a Radix dialog lives in a portal, which a plain render never reaches.
 */
export function CreateIdentityFromFacesForm({
  faceCount,
  draft,
  onDraftChange,
  onSubmit,
  onCancel,
  isSubmitting,
  error,
  copy = 'identity.unassigned.create',
}: {
  faceCount: number
  draft: CreateIdentityDraft
  onDraftChange: (draft: CreateIdentityDraft) => void
  onSubmit: () => void
  onCancel: () => void
  isSubmitting: boolean
  error: string | null
  copy?: string
}) {
  const { t } = useTranslation()
  const named = hasIdentityName(draft)

  return (
    <>
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="manual-identity-first-name">
            {t('identity.detail.fields.firstName')}
          </Label>
          <Input
            id="manual-identity-first-name"
            value={draft.firstName}
            onChange={event => onDraftChange({ ...draft, firstName: event.target.value })}
            placeholder={t('identity.unassigned.create.firstNamePlaceholder')}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-identity-last-name">{t('identity.detail.fields.lastName')}</Label>
          <Input
            id="manual-identity-last-name"
            value={draft.lastName}
            onChange={event => onDraftChange({ ...draft, lastName: event.target.value })}
            placeholder={t('identity.unassigned.create.lastNamePlaceholder')}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="manual-identity-nickname">{t('identity.detail.fields.nickname')}</Label>
          <Input
            id="manual-identity-nickname"
            value={draft.nickname}
            onChange={event => onDraftChange({ ...draft, nickname: event.target.value })}
            placeholder={t('identity.unassigned.create.nicknamePlaceholder')}
          />
        </div>
        {!named ? (
          <p data-testid="create-identity-hint" className="text-xs text-muted-foreground">
            {t('identity.unassigned.create.nameRequiredHint')}
          </p>
        ) : null}
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          data-testid="create-identity-submit"
          onClick={onSubmit}
          disabled={isSubmitting || faceCount === 0 || !named}
        >
          {t(`${copy}.submit`)}
        </Button>
      </DialogFooter>
    </>
  )
}

/** The draft as the API takes it — trimmed, and a blank field left out. */
export function identityNamesFromDraft(draft: CreateIdentityDraft): {
  first_name?: string
  last_name?: string
  nickname?: string
} {
  return {
    first_name: draft.firstName.trim() || undefined,
    last_name: draft.lastName.trim() || undefined,
    nickname: draft.nickname.trim() || undefined,
  }
}
